/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment';
import Router from 'next/router';
import Auth from '@ion/applications/src/authentication/AuthenticationManager';
import {
  PreferencesStore
} from '@ion/core/src/preferences/types';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {config, url} from '@ion/core/src/services/ConnectUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {contains} from '@ion/core/src/utils/StringUtil';
import {
  resolvePreferences as resolve,
  ResolverResults,
  setTemporaryPreferences
} from './TemporaryPreferencesStore';

/**
 * Provides an internal flag which provides the initialization state of the
 * `UserPreferencesAutoSave` Feature, which is only event to be initialized
 * once per session.
 *
 * By default, this value remains `false` until the `initialize` function is
 * invoked, at which point, it will be set to `true` and remain enabled for
 * the duration of the session.
 */
let _initialized = false;

/**
 * @private
 *
 * Provides a reference to the session timeout handle that is used for clearing
 * the timeout when completed.
 */
let _sessionTimeoutHandle;

/**
 * Provides an internal flag which is used to determine if the user's session
 * is currently active. By default, this value is set to `true`, and remains
 * set to `true`, and is only ever set to `false` when the `sessionExpired`
 * function is invoked - which, should only be invoked when the user's session
 * has expired.
 *
 * This flag is used to persist the user's preferences to local storage if
 * there are changes which could not be saved prior to the session time out,
 * which can occur due to application inactivity, or the browser and / or
 * system becoming idle.
 */
let _isAuthenticated = true;

/**
 * Provides a reference to the Id of the User Preferences for which preferences
 * are to be persisted.
 */
let _userId: number;

/**
 * @internal
 *
 * Provides the Auto Save feature which is invoked when either the page changes
 * and `AutoSaveEnablement.PAGE_CHANGE` feature is enabled, or when the user's
 * session is about to expire and the `AutoSaveEnablement.SESSION_EXPIRE` feature
 * is enabled.
 */
const autoSave = () => {
  if (UserPreferences.hasChanges(true)) {
    UserPreferences.save();
  }
};

/**
 * @internal
 *
 * Provides the mechanism for automatically saving `UserPreferences` when the
 * user exits the page in any way, at which point if there are unsaved changes
 * this function wil fire off a keepalive request to save the users preferences
 * when the application is unloaded either via reloading the page, closing the
 * browser tab, closing the browser, navigating away from the page, etc..
 */
const onExitAutoSave = (userId: number) => () => {
  if (UserPreferences.hasChanges(true)) {
    setTemporaryPreferences(_userId, UserPreferences.getStore(), true);
    fetch(url(`/users/${userId}/extendedattributes`), {
      keepalive   : true,
      method      : 'PUT',
      mode        : 'cors',
      credentials : 'include',
      headers     : {
        ...config.headers
      },
      body : safeJSONStringify({
        value : safeJSONStringify(UserPreferences?.getStore())
      })
    });
  }
};

/**
 * @internal
 *
 * Schedules the `autoSave` invocation based on the user's authorization token
 * and the current token expiration, at which point at timeout will be created
 * for the duration of the session and invoked approx. 2 minutes before the user
 * session expired.
 *
 * @param expiration The authorization session token's expiration as determined
 *        via the `Auth.getExpiration()` API. If the token is unavailable, or if
 *        the expiration is unavailable, the session automated save feature will
 *        be disabled and a warning will be logged.
 */
const scheduleSessionAutoSave = async() => {
  const expiration = await Auth.getExpiration();
  const expiry = moment(expiration);
  if (expiry?.isValid()) {
    const scheduled = expiry.subtract(2, 'minutes');
    const delay = scheduled.diff(moment());
    if (delay > 0) {
      _sessionTimeoutHandle = setTimeout(() => {
        clearTimeout(_sessionTimeoutHandle);
        autoSave();
      }, delay);
    }
  }
};

/**
 * @internal
 *
 * Provides a utility for determining which auto save features are to be enabled.
 *
 * @param   enablement An array of auto-save feature enablements
 * @returns the enabled features based on the provided array of enablements.
 */
const getEnablements = (enablements: AutoSaveEnablement[]): {
  allEnabled: boolean,
  sessionExpired: boolean,
  pageChange: boolean,
  appExit: boolean,
} => {
  const items = enablements.toString();
  const all   = contains(items, AutoSaveEnablement.ALL);
  return {
    pageChange     : all || contains(items, AutoSaveEnablement.PAGE_CHANGE),
    sessionExpired : all || contains(items, AutoSaveEnablement.SESSION_EXPIRE),
    appExit        : all || contains(items, AutoSaveEnablement.APP_EXIT),
    allEnabled     : all
  };
};

/**
 * Given a `User` Id, `lastModifiedOn` date, and `lastSavedOn` date, determines
 * if there are temporarily persisted user preferences, and if they have been saved
 * locally after the User's `extendedAttributes` have last been saved, in which case
 * this indicates that the latest user preferences have not been saved, and the
 * locally persisted preferences are to be used (and immediately save to CTC),
 * otherwise, the current User Preferences as obtained from the extendedAttributes
 * will be returned.
 *
 * ```typescript
 *  // packages/<ion-app>/store/actions/initialization.ts
 *  ...
 *
 *  const initializePreferences = async(user: User, readOnly = false) => {
 *    // references the retrieved user preferences from ctc ...
 *    const {preferences, lastModifiedOn} = UserPreferences.fromExtendedAttributes(user);
 *
 *    // resolve the user preferences either to those returned by ctc (if latest)
 *    // or previously persisted to local storage (if persisted after saved to ctc) ...
 *    const {preferences: current, isStale}: PreferencesStore = resolvePreferences(
 *      user.id, lastModifiedOn, preferences
 *    );
 *    // initialize the user preferences api ...
 *    await UserPreferences.initialize(
 *      user.id,
 *      current,
 *      ApplicationKeys.CTCAdmin,
 *      readOnly,
 *      true
 *    );
 *    // optionally, initialize the user preferences auto-save feature (recommended) ...
 *    initializeUserPreferencesAutoSave(user.id);
 *
 *    // if the resolved preferences are stale; i.e. they haven't been saved to the
 *    // platform, save the preferences ...
 *    if (isStale) {
 *      UserPreferences.save();
 *    }
 *  };
 * ```
 *
 * @param   userId The id of the `User` for which the temporary preferences are to be
 *          resolved.
 * @param   The `lastModifiedOn` value of the user `extendedAttributes` from which the
 *          preferences have been retrieved.
 * @param   preferences The current User Preferences retrieved from CTC via the User's
 *          `extendedAttributes`
 * @returns The results of the preferences resolution, which contain the preferences,
 *          and an `isStale` property which indicates how the preferences were resolved.
 *          When `isStale` is `true`, the preferences were restored from localStorage,
 *          and is used to indicate that the User Preferences should be immediately
 *          saved. If `false`, indicates that the preferences from the Platform are
 *          the most current and need not be saved.
 */
export const resolvePreferences = (
  userId: number, lastModifiedOn: string, preferences: PreferencesStore): ResolverResults => (
  resolve(userId, lastModifiedOn, preferences)
);

/**
 * To be invoked when the User's Session has expired, at which point, if there
 * are unsaved User Preferences, the current preferences will be persisted locally
 * and subsequently restored upon the next login if the preferences which have
 * been persisted locally were saved after the current external preferences
 * were last saved to the platform.
 *
 * ```typescript
 *  // packages/<ion-app>/store/actions/login.ts
 *  export const sessionTimeout = () => {
 *    if (UserPreferences.hasChanges(true)) {
 *      setSessionExpired();
 *    }
 *    return () => {
 *      deleteLocalStorage(ApplicationKeys.CTCAdminSession);
 *      location.assign(getRoute(Route.LOGIN));
 *    };
 *  };
 * ```
 *
 * @param   userId The id of the `User` for which the temporary preferences are to
 *          be persisted to localStorage when the user's session has expired.
 */
export const setSessionExpired = () => {
  _isAuthenticated = false;
  setTemporaryPreferences(_userId, UserPreferences.getStore());
};

/**
 * Defines the specific User Preference Auto Save Features that can be enabled
 * during initialization. Clients can set the optional `enablement` argument of
 * the `initialize` function based on their needs. The mappings of enablements
 * are as follows:
 *
 *  - `ALL`            : Specifies that all auto-save features are to be enabled.
 *  - `PAGE_CHANGE`    : Specifies that auto saving is to be performed when the
 *                       User Navigates to another Page.
 *  - `SESSION_EXPIRE` : Specifies that auto saving is to be performed just prior
 *                       to the User's Session Expiring.
 *  - `APP_EXIT`       : Specifies that auto saving is to be performed when the
 *                       User Exits the Application by reloading the page, closing
 *                       the browser and / or tab, or navigates to another site,
 */
export enum AutoSaveEnablement {
  ALL = 'ALL',
  PAGE_CHANGE = 'PAGE_CHANGE',
  SESSION_EXPIRE = 'SESSION_EXPIRE',
  APP_EXIT = 'APP_EXIT'
}

/**
 * The `UserPreferencesAutoSave` Feature provides enhanced persistence capabilities
 * for the `UserPreferences` API which allows applications to omit explicitly saving
 * preferences and delegate this responsibility to the `UserPreferencesAutoSave` API.
 *
 * The API ensures that if there have been changes to the User's Preferences since the
 * last time they were saved, the preferences will automatically be saved when any of
 * the following occurs:
 *
 *  - The User Navigates to another Page.
 *  - The User's Session is about to Expire.
 *  - The User Exits the Application by reloading the page, closing the browser and
 *    / or tab, or navigates to another site,
 *
 * This feature requires `UserPreferences` change detection to be enabled, thus, if
 * it has not been enabled during initialization of the `UserPreferences` API, it
 * will automatically be enabled via this API.
 *
 * ```typescript
 *   // packages/app/src/store/actions/initialization.ts
 *   import initializeAutoSave from '@ion/applications/src/preferences';
 *   import UserPreferences from '@ion/core/src/preferences/UserPreferences';
 *   ...
 *
 *   initializePreferences = async(user: User, readOnly = false) => {
 *     ...
 *     await UserPreferences.initialize(
 *       user.id, // userId
 *       extendedAttributes, // optional value
 *       ApplicationKeys.SomeApp, // optional namespace
 *       readOnly, // optional readOnly enabling
 *       true // optional enableChangeDetection
 *     )
 *     initializeAutoSave(user.id);
 *   };
 *
 * ```
 *
 * @param   userId The Id of the user for which preferences are to be automatically
 *          saved.
 * @param   enablement Optional array of auto-save feature enablements which allows
 *          for specifying which autosave features are to be enabled. Defaults to
 *          `AutoSaveEnablement.ALL`, thus all auto saving features will be enabled
 *          by default.
 * @returns `true` if the `UserPreferencesAutoSave` Feature has been initialized
 *          otherwise, returns `false` if the auto save feature has been initialized
 *          previously.
 */
const initialize = (
  userId: number, enablements: AutoSaveEnablement[] = [AutoSaveEnablement.ALL]): boolean => {
  if (!_initialized) {
    _userId = userId;
    if (!UserPreferences.hasChangeDetection()) {
      UserPreferences.enableChangeDetection();
    }
    const {
      allEnabled, pageChange, sessionExpired, appExit
    } = getEnablements(enablements);
    if (allEnabled || pageChange) {
      Router.events.on('routeChangeComplete', autoSave);
    }
    if (allEnabled || appExit) {
      globalThis.onbeforeunload = onExitAutoSave(userId);
    }
    if (allEnabled || sessionExpired) {
      scheduleSessionAutoSave();
    }
    return (_initialized = true);
  }
  return false;
};

/**
 * @private
 *
 * Internal function which is used for resetting the `UserPreferencesAutoSave`
 * state.
 *
 * **Note: This function is generally never to be invoked, and is only provided
 * for specific internal use-cases**.
 */
export const __reset__ = () => {
  _initialized = false;
  _isAuthenticated = true;
  _userId = undefined;
  Router.events.off('routeChangeComplete', autoSave);
  globalThis.onbeforeunload = undefined;
  clearTimeout(_sessionTimeoutHandle);
};

export default initialize;
